import { apiGet, apiPost, apiPut, apiDelete } from './BaseService';

export interface Package {
  _id: string;
  packageName: string;
  scheduleCount: number;
  perScheduleBasePrice: number;
  ticketCountPerSchedule: number;
  perTicketBasePrice: number;
  totalPrice: number;
  isCustom: boolean;
  isRestricted: boolean;
  createdBy: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentDetails {
  _id: string;
  upiId: string;
  upiQrCode?: string;
  bankName?: string;
  accountNumber?: string;
  ifscCode?: string;
  accountHolderName?: string;
  isActive: boolean;
}

export interface PaymentTransaction {
  _id: string;
  groupId: string;
  packageId: string;
  amount: number;
  paymentMethod: 'upi' | 'netbanking';
  transactionId?: string;
  paymentProof?: string;
  status: 'pending' | 'verification-pending' | 'approved' | 'rejected';
  requestedBy: string;
  createdAt: string;
}

export interface GroupCredits {
  hasActivePackage: boolean;
  packageName?: string;
  totalCredits: {
    scheduleCredits: number;
    ticketCredits: number;
  };
  usedCredits: {
    scheduleCredits: number;
    ticketCredits: number;
  };
  remainingCredits: {
    scheduleCredits: number;
    ticketCredits: number;
  };
}

class PackageService {
  // Get all packages (predefined only)
  async getPackages(): Promise<Package[]> {
    return await apiGet('/packages');
  }

  // Get all packages including custom (Super Admin)
  async getAllPackages(): Promise<Package[]> {
    return await apiGet('/packages/all');
  }

  // Create predefined package (Super Admin)
  async createPackage(packageData: Partial<Package>): Promise<Package> {
    return await apiPost('/packages', packageData);
  }

  // Create custom package
  async createCustomPackage(scheduleCount: number, ticketCountPerSchedule: number): Promise<Package> {
    return await apiPost('/packages/custom', { scheduleCount, ticketCountPerSchedule });
  }

  // Calculate package price
  async calculatePrice(scheduleCount: number, ticketCountPerSchedule: number): Promise<any> {
    return await apiPost('/packages/calculate-price', { scheduleCount, ticketCountPerSchedule });
  }

  // Get custom package settings
  async getCustomPackageSettings(): Promise<any> {
    return await apiGet('/packages/custom-settings');
  }

  // Set custom package settings (Super Admin)
  async setCustomPackageSettings(perScheduleBasePrice: number, perTicketBasePrice: number): Promise<any> {
    return await apiPost('/packages/custom-settings', { perScheduleBasePrice, perTicketBasePrice });
  }

  // Get package by ID
  async getPackageById(id: string): Promise<Package> {
    return await apiGet(`/packages/${id}`);
  }

  // Update package (Super Admin)
  async updatePackage(id: string, packageData: Partial<Package>): Promise<Package> {
    return await apiPut(`/packages/${id}`, packageData);
  }

  // Delete package (Super Admin)
  async deletePackage(id: string): Promise<any> {
    return await apiDelete(`/packages/${id}`);
  }

  // Get payment details
  async getPaymentDetails(): Promise<PaymentDetails> {
    return await apiGet('/payment/details');
  }

  // Set payment details (Super Admin)
  async setPaymentDetails(paymentData: Partial<PaymentDetails>): Promise<PaymentDetails> {
    return await apiPost('/payment/details', paymentData);
  }

  // Generate QR code for UPI payment
  async generateQRCode(amount: number, upiId: string): Promise<{ qrCodeUrl: string }> {
    return await apiGet(`/payment/generate-qr?amount=${amount}&upiId=${upiId}`);
  }

  // Submit payment request
  async submitPaymentRequest(paymentData: {
    packageId: string;
    groupId: string;
    amount: number;
    paymentMethod: 'upi' | 'netbanking';
    transactionId?: string;
    paymentProof?: string;
    status?: string;
  }): Promise<PaymentTransaction> {
    return await apiPost('/payment/request', paymentData);
  }

  // Update payment request
  async updatePaymentRequest(transactionId: string, updateData: {
    transactionId?: string;
    paymentMethod?: string;
    paymentProof?: string;
  }): Promise<PaymentTransaction> {
    return await apiPut(`/payment/request/${transactionId}`, updateData);
  }

  // Get pending payment requests (Super Admin)
  async getPendingPayments(queryParams?: string): Promise<{requests: PaymentTransaction[], total: number}> {
    const url = queryParams ? `/payment/requests?${queryParams}` : '/payment/requests';
    return await apiGet(url);
  }

  // Get payment history (Super Admin)
  async getPaymentHistory(queryParams?: string): Promise<{transactions: PaymentTransaction[], total: number}> {
    const url = queryParams ? `/payment/history?${queryParams}` : '/payment/history';
    return await apiGet(url);
  }

  // Approve payment (Super Admin)
  async approvePayment(transactionId: string): Promise<any> {
    return await apiPut(`/payment/approve/${transactionId}`, {});
  }

  // Reject payment (Super Admin)
  async rejectPayment(transactionId: string, rejectionReason: string): Promise<any> {
    return await apiPut(`/payment/reject/${transactionId}`, { rejectionReason });
  }

  // Get group credits
  async getGroupCredits(groupId: string): Promise<GroupCredits> {
    return await apiGet(`/groups/${groupId}/credits`);
  }

  // Use credits
  async useCredits(groupId: string, scheduleCredits: number, ticketCredits: number): Promise<any> {
    return await apiPost(`/groups/${groupId}/use-credits`, { scheduleCredits, ticketCredits });
  }

  // Get group packages (current subscription and pending transactions)
  async getGroupPackages(groupId: string): Promise<any> {
    return await apiGet(`/groups/${groupId}/packages`);
  }

  // Get group usage statistics
  async getGroupUsage(groupId: string): Promise<any> {
    return await apiGet(`/groups/${groupId}/usage`);
  }

  // Select package for group
  async selectPackageForGroup(groupId: string, packageId: string): Promise<any> {
    return await apiPost(`/groups/${groupId}/packages/${packageId}/select`, {});
  }

  // Get countries
  async getCountries(): Promise<{countryCode: string, name: string}[]> {
    return await apiGet('/countries');
  }

  // Get states by country
  async getStates(countryCode: string): Promise<{stateCode: string, name: string}[]> {
    return await apiGet(`/states/country/${countryCode}`);
  }

  // Get all groups
  async getGroups(): Promise<{_id: string, name: string}[]> {
    return await apiGet('/groups');
  }
}

export default new PackageService();